/**
 * Groups Service
 * API service for user groups (Groupes de diffusion)
 */

import { AxiosError } from "axios";
import axiosInstance from "@/axiosConfig";
import { logger } from "@/shared/services/sentry.service";
import type { AffectedResourcesReport } from "@/shared/types/affectedResources.types";
import { getApiUrl } from "@/shared/utils/api";
import { buildSearchParams } from "@/shared/utils/query-params";
import { GROUPS_CONSTANTS } from "../configs/constants";
import { GROUPS_ENDPOINTS } from "../configs/endpoints";
import type {
  UserGroup,
  UserGroupsResponse,
  UserGroupQueryParams,
  CreateUserGroupPayload,
  UpdateUserGroupPayload,
  GroupMembersPayload,
  MemberType,
} from "./groups.service.types";

// Re-export types for convenience
export type {
  UserGroup,
  UserGroupsResponse,
  UserGroupQueryParams,
  CreateUserGroupPayload,
  UpdateUserGroupPayload,
  MemberType,
  GroupMember,
  GroupsMetadata,
} from "./groups.service.types";

/** Supported member types for group operations (excludes beneficiary) */
type SupportedMemberType = Exclude<MemberType, "beneficiary">;

/** Guards membership operations against empty payloads (would leak all groups or issue a no-op PUT). */
const hasAnyMemberId = (members: GroupMembersPayload): boolean =>
  Boolean(
    members.employee_ids?.length
    || members.representative_ids?.length
    || members.external_contact_ids?.length,
  );

/**
 * Configuration mapping for member type operations.
 * Centralizes endpoint paths and payload keys to eliminate switch statements.
 */
const MEMBER_TYPE_CONFIG: Record<SupportedMemberType, {
  endpoint: string
  payloadKey: string
  idsKey: keyof GroupMembersPayload
}> = {
  employee: {
    endpoint: "/employees",
    payloadKey: "employee",
    idsKey: "employee_ids",
  },
  representative: {
    endpoint: "/representatives",
    payloadKey: "representative",
    idsKey: "representative_ids",
  },
  external_contact: {
    endpoint: "/external_contacts",
    payloadKey: "external_contact",
    idsKey: "external_contact_ids",
  },
};

export class GroupsService {
  /**
   * Fetch all user groups with pagination and filtering
   */
  static async getGroups(params?: UserGroupQueryParams): Promise<UserGroupsResponse> {
    const searchParams = buildSearchParams(params, { defaults: { limit: GROUPS_CONSTANTS.DEFAULT_PAGE_SIZE } });

    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${GROUPS_ENDPOINTS.GET_GROUPS}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<UserGroupsResponse>(url);
    return response.data;
  }

  /**
   * Create a new user group
   */
  static async createGroup(payload: CreateUserGroupPayload): Promise<UserGroup> {
    // The create endpoint renders the same `show` view as GET, so the group is
    // wrapped under a `user_group` root key. Unwrap it so callers get a bare
    // `UserGroup` with `id` (otherwise the create-then-add-members flow PUTs to
    // `/user_groups/undefined`).
    const response = await axiosInstance.post<{ user_group: UserGroup }>(
      getApiUrl(GROUPS_ENDPOINTS.CREATE_GROUP),
      payload,
    );
    return response.data.user_group;
  }

  /**
   * Get a specific user group by ID
   */
  static async getGroup(id: string, fields?: string): Promise<{ user_group: UserGroup }> {
    const searchParams = buildSearchParams({ fields });
    const queryString = searchParams.toString();
    const url = getApiUrl(
      `${GROUPS_ENDPOINTS.GET_GROUP(id)}${queryString ? `?${queryString}` : ""}`,
    );

    const response = await axiosInstance.get<{ user_group: UserGroup }>(url);
    return response.data;
  }

  /**
   * ADM-856: read the resources this group is still affected to, grouped by type.
   * Agnostic of the group `kind`. The very same payload comes back in the 409 that
   * refuses to delete a group still in use.
   */
  static async getAffectedResources(id: string): Promise<AffectedResourcesReport> {
    const response = await axiosInstance.get<AffectedResourcesReport>(
      getApiUrl(GROUPS_ENDPOINTS.AFFECTED_RESOURCES(id)),
    );
    return response.data;
  }

  /**
   * Update a specific user group
   * @param confirmed - ADM-856: acknowledges the impact on dynamic groups reported
   *   by a prior 409; never send this upfront, the server recompute is the guard
   */
  static async updateGroup(id: string, payload: UpdateUserGroupPayload, confirmed = false): Promise<UserGroup> {
    const body = confirmed ? { ...payload, confirmed: true } : payload;
    const response = await axiosInstance.put<UserGroup>(
      getApiUrl(GROUPS_ENDPOINTS.UPDATE_GROUP(id)),
      body,
    );
    return response.data;
  }

  /**
   * Delete a specific user group
   * @param confirmed - ADM-856: see updateGroup
   */
  static async deleteGroup(id: string, confirmed = false): Promise<void> {
    await axiosInstance.delete(
      getApiUrl(GROUPS_ENDPOINTS.DELETE_GROUP(id)),
      confirmed ? { params: { confirmed: true } } : undefined,
    );
  }

  /**
   * Acknowledge a group flagged `needs_review`, clearing the badge without
   * touching its filters. Refused (422) from any other status — see
   * user_groups_controller.rb#acknowledge.
   */
  static async acknowledgeGroup(id: string): Promise<UserGroup> {
    const response = await axiosInstance.post<{ user_group: UserGroup }>(
      getApiUrl(GROUPS_ENDPOINTS.ACKNOWLEDGE_GROUP(id)),
    );
    return response.data.user_group;
  }

  /**
   * Delete multiple user groups by their IDs
   */
  static async deleteGroupsInBatch(ids: string[]): Promise<{ results: string }> {
    const response = await axiosInstance.post<{ results: string }>(
      getApiUrl(GROUPS_ENDPOINTS.DELETE_GROUPS_BATCH),
      { ids },
    );
    return response.data;
  }

  /**
   * Update member's user groups directly
   */
  static async updateMemberGroups(
    memberId: string,
    memberType: SupportedMemberType,
    userGroupIds: string[],
  ): Promise<{ data: unknown }> {
    const config = MEMBER_TYPE_CONFIG[memberType];
    const url = getApiUrl(`${config.endpoint}/${memberId}`);
    const payload = { [config.payloadKey]: { user_group_ids: userGroupIds } };
    return axiosInstance.put(url, payload);
  }

  /**
   * Add members to a group (preserving existing members)
   */
  static async addMembersToGroup(
    groupId: string,
    memberIds: string[],
    memberType: "employee" | "representative" | "external_contact",
  ): Promise<UserGroup> {
    // First, get the current group details to retrieve existing members
    const currentGroupResponse = await GroupsService.getGroup(groupId);
    const currentGroup = currentGroupResponse.user_group;

    // Get existing member IDs based on member type
    const existingMemberIds: string[] = [];
    if (currentGroup.members) {
      currentGroup.members.forEach((member) => {
        if (member.member_type === memberType) {
          existingMemberIds.push(member.id);
        }
      });
    }

    // Combine existing members with new members (avoid duplicates)
    const allMemberIds = [...new Set([...existingMemberIds, ...memberIds])];

    // Prepare payload using config mapping
    const config = MEMBER_TYPE_CONFIG[memberType];
    const payload: UpdateUserGroupPayload = { user_group: { [config.idsKey]: allMemberIds } };

    // Update the group with all members
    const response = await GroupsService.updateGroup(groupId, payload);
    return response;
  }

  /**
   * Fetch the groups that contain at least one of the given members.
   * Accepts several member types at once (union / OR semantics).
   *
   * Backed by the groups `search` index action. A `404` means one of the ids is
   * malformed or belongs to another organisation — treated as "no matching
   * group" so the caller keeps the previous empty-list behaviour instead of
   * surfacing an error.
   */
  static async getGroupsContainingMembers(members: GroupMembersPayload): Promise<UserGroupsResponse> {
    if (!hasAnyMemberId(members)) {
      throw new Error("getGroupsContainingMembers requires at least one member id");
    }

    try {
      const response = await axiosInstance.post<UserGroupsResponse>(
        getApiUrl(GROUPS_ENDPOINTS.SEARCH),
        members,
      );
      return response.data;
    }
    catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        logger.warn("getGroupsContainingMembers: search returned 404, treating as no match", { members });
        return { user_groups: [], metadata: { total_items: 0, current_page: 1, total_pages: 0 } };
      }
      throw error;
    }
  }

  /**
   * Remove members from a group while preserving the other members.
   * Mirrors `addMembersToGroup`: for each provided member type, the group is
   * updated with the remaining ids of that type (full-replacement semantics).
   * Only the provided member types are touched.
   *
   * The remaining ids are always computed from an authoritative GET of the
   * group: a partial or search-sourced member list would silently drop the
   * members it omits. Note: full-replacement still carries a residual
   * lost-update risk under concurrent edits — a server-side atomic remove
   * endpoint would be required to close it entirely.
   */
  static async removeMembersFromGroup(
    groupId: string,
    membersToRemove: GroupMembersPayload,
  ): Promise<UserGroup> {
    if (!hasAnyMemberId(membersToRemove)) {
      throw new Error("removeMembersFromGroup requires at least one member id");
    }

    const currentMembers = (await GroupsService.getGroup(groupId)).user_group.members ?? [];

    const payload: GroupMembersPayload = {};
    (Object.keys(MEMBER_TYPE_CONFIG) as SupportedMemberType[]).forEach((memberType) => {
      const { idsKey } = MEMBER_TYPE_CONFIG[memberType];
      const idsToRemove = membersToRemove[idsKey];
      if (!idsToRemove?.length) return;

      const removedIds = new Set(idsToRemove);
      payload[idsKey] = currentMembers
        .filter((member) => member.member_type === memberType && !removedIds.has(member.id))
        .map((member) => member.id);
    });

    return GroupsService.updateGroup(groupId, { user_group: payload });
  }
}

/**
 * Backward compatibility wrapper that maps old method names to new ones
 */
export const UserGroupsService = {
  getUserGroups: GroupsService.getGroups,
  createUserGroup: GroupsService.createGroup,
  getUserGroup: GroupsService.getGroup,
  updateUserGroup: GroupsService.updateGroup,
  deleteUserGroup: GroupsService.deleteGroup,
  deleteUserGroupsInBatch: GroupsService.deleteGroupsInBatch,
  updateMemberUserGroups: GroupsService.updateMemberGroups,
  addMembersToGroup: GroupsService.addMembersToGroup,
};
